import { useState, useMemo } from 'react'
import { Drawer, Input, VStack, Box, Text, Portal, CloseButton } from '@chakra-ui/react';

export interface PickerOption { value: string; label: string; group?: string; sublabel?: string }

interface PickerSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  options: PickerOption[]
  selectedValue?: string
  onSelect: (value: string) => void
  searchable?: boolean
}

export function PickerSheet({ isOpen, onClose, title, options, selectedValue, onSelect, searchable }: PickerSheetProps) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => o.label.toLowerCase().includes(q))
  }, [options, query])

  const grouped = useMemo(() => {
    const map = new Map<string, PickerOption[]>()
    for (const o of filtered) {
      const key = o.group ?? ''
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(o)
    }
    return [...map.entries()]
  }, [filtered])

  return (
    <Drawer.Root open={isOpen} placement='bottom' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px">{title}</Drawer.Header>
            <Drawer.Body>
              {searchable && (
                <Input placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} mb={3} mt={2} />
              )}
              <VStack gap={3} align="stretch" pb={2}>
                {grouped.map(([group, opts]) => (
                  <Box key={group || 'ungrouped'}>
                    {group && <Text fontSize="2xs" fontWeight="bold" color="gray.400" textTransform="uppercase" mb={1}>{group}</Text>}
                    <VStack gap={1} align="stretch">
                      {opts.map(o => (
                        <Box
                          textAlign="left"
                          minH="48px"
                          px={3}
                          py={2}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={o.value === selectedValue ? 'accent' : 'gray.200'}
                          bg={o.value === selectedValue ? 'accent.soft' : 'white'}
                          _active={{ bg: 'gray.50' }}
                          asChild><button key={o.value} onClick={() => { onSelect(o.value); onClose() }}>
                            <Text fontSize="sm">{o.label}</Text>
                            {o.sublabel && <Text fontSize="xs" color="gray.500">{o.sublabel}</Text>}
                          </button></Box>
                      ))}
                    </VStack>
                  </Box>
                ))}
                {filtered.length === 0 && <Text fontSize="sm" color="gray.400">No matches</Text>}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
